import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Megaphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { fcfa, fdatetime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, Loader, PageTitle } from "@/components/common";
import { KpiCards } from "@/components/console/Kpis";

export const ExportButton = ({ path, label, testId }) => (
  <a data-testid={testId} href={`${API}${path}`} className="mr-btn-secondary px-3 inline-flex"><Download size={15} /> {label}</a>
);

const OP_LABEL = { brouillon: "Brouillons", soumis: "À valider", publie: "Publiées", adhesions_closes: "Adhésions closes", en_execution: "En exécution", cloture: "Clôturées", annulee: "Annulées", suspendue: "Suspendues" };

export function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  if (!s) return <Loader />;
  const cards = [
    ["Membres actifs", s.members.actif, "dash-active", "/console/membres"], ["Suspendus", s.members.suspendu, "dash-suspended", "/console/membres"],
    ["Candidatures en cours", s.applications_pending, "dash-candidates", "/console/admission"], ["Prospects nouveaux", s.prospects_new, "dash-requests", "/console/demandes"],
    ["Cotisations dues (30 j)", s.contributions_due, "dash-dues", "/console/cotisations"], ["Encaissements du mois", fcfa(s.receipts_month), "dash-receipts", "/console/finances"],
    ["Paiements à valider", s.payments_pending + s.installments_declared, "dash-pending", "/console/cotisations"], ["Signalements ouverts", s.reports_open, "dash-reports", "/console/moderation"],
    ["Membres actifs 7 j", s.engagement.active_members_7d, "dash-engagement", null], ["Messages 7 j", s.engagement.messages_7d, "dash-messages", null], ["Publications 7 j", s.engagement.posts_7d, "dash-posts", null], ["Opérations à valider", s.operations_to_validate, "dash-ops-validate", "/console/operations"],
  ];
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Console" title="Tableau de bord" />
      <KpiCards section="dashboard" />
      <h2 className="mr-eyebrow mb-3">À traiter maintenant</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-mr-border border border-mr-border">
        {cards.map(([l, v, id, to]) => {
          const inner = <><p className="text-xs uppercase tracking-wider text-mr-muted">{l}</p><p className="font-display font-bold text-3xl tabular mt-2">{v}</p></>;
          return to ? <Link key={id} to={to} className="bg-mr-surface p-5 hover:bg-mr-raised transition-colors" data-testid={id}>{inner}</Link> : <div key={id} className="bg-mr-surface p-5" data-testid={id}>{inner}</div>;
        })}
      </div>
      <h2 className="mr-eyebrow mt-8 mb-3">Opérations par statut</h2>
      <div className="flex flex-wrap gap-2" data-testid="dash-ops-status">
        {Object.entries(s.operations_by_status).map(([k, n]) => <span key={k} className="px-3 py-1.5 border border-mr-border rounded-full text-sm">{OP_LABEL[k] || k} <b className="tabular">{n}</b></span>)}
      </div>
    </div>
  );
}

const ACTIONS = [["rejeter", "Rejeter"], ["masquer", "Masquer"], ["supprimer", "Supprimer"], ["avertir", "Avertir"], ["suspendre", "Suspendre"]];

export function Moderation() {
  const [status, setStatus] = useState("ouvert");
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/reports", { params: { status } }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  const decide = async (r, action) => {
    const note = window.prompt("Note (facultatif, envoyée au membre en cas d'avertissement)") ?? null;
    if (note === null) return;
    try {
      await api.post(`/admin/reports/${r.id}/decision`, { action, note });
      toast.success("Décision enregistrée");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Modération" title="Signalements" />
      <div className="flex gap-1 mb-4 border-b border-mr-border">
        {[["ouvert", "Ouverts"], ["traite", "Traités"], ["rejete", "Rejetés"]].map(([k, l]) => (
          <button key={k} data-testid={`reports-tab-${k}`} onClick={() => setStatus(k)} className={`px-3 h-10 text-sm border-b-2 -mb-px ${status === k ? "border-mr-accent" : "border-transparent text-mr-muted"}`}>{l}</button>
        ))}
      </div>
      {items === null ? <Loader /> : items.length === 0 ? <EmptyState title="Aucun signalement" text="Les messages, publications et profils signalés arrivent ici avec leur contexte." testId="reports-empty" /> : (
        <ul className="space-y-3">
          {items.map((r) => (
            <li key={r.id} className="mr-card p-4 space-y-2" data-testid="report-row">
              <p className="text-xs text-mr-muted">{fdatetime(r.created_at)} · {r.object_type === "message" ? "Message" : r.object_type === "post" ? "Publication" : "Profil"} · signalé par {r.reporter_name}</p>
              <p className="text-sm"><b>Motif :</b> {r.reason}</p>
              <p className="text-sm"><b>Auteur :</b> {r.target.user_name}{r.target.status && r.target.status !== "envoye" && r.target.status !== "publie" ? ` · contenu ${r.target.status}` : ""}</p>
              {r.target.text && <blockquote className="text-sm border-l-2 border-mr-danger pl-3 text-mr-text2">{r.target.text}</blockquote>}
              {r.context.length > 0 && (
                <details className="text-xs"><summary className="cursor-pointer text-mr-muted">Contexte de la conversation ({r.context.length})</summary>
                  <ul className="mt-2 space-y-1">{r.context.map((c) => <li key={c.id} className={c.id === r.object_id ? "font-semibold" : ""}>{c.sender_name} : {c.text}</li>)}</ul></details>
              )}
              {r.decision && <p className="text-xs text-mr-accent">Décision : {r.decision}</p>}
              {status === "ouvert" && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {ACTIONS.filter(([a]) => r.object_type !== "user" || !["masquer", "supprimer"].includes(a)).map(([a, l]) => (
                    <button key={a} data-testid={`report-${a}`} className={a === "suspendre" ? "mr-btn-secondary px-3 text-mr-danger" : "mr-btn-secondary px-3"} onClick={() => decide(r, a)}>{l}</button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MatrixEditor() {
  const [cats, setCats] = useState(null);
  useEffect(() => {
    api.get("/admin/notification-matrix").then(({ data }) => setCats(data));
  }, []);
  if (!cats) return <Loader />;
  const set = (k, c, v) => setCats(cats.map((x) => (x.key === k ? { ...x, channels: { ...x.channels, [c]: v } } : x)));
  const save = async () => {
    try {
      const { data } = await api.put("/admin/notification-matrix", { matrix: Object.fromEntries(cats.map((c) => [c.key, c.channels])) });
      setCats(data);
      toast.success("Configuration enregistrée");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="mr-card p-4 mb-8" data-testid="notification-matrix">
      <h2 className="font-medium mb-1">Notifications : configuration par défaut</h2>
      <p className="text-xs text-mr-muted mb-3">Les membres peuvent couper un canal par catégorie, sauf sécurité et paiements. WhatsApp n'est pas utilisé pour le moment.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-mr-muted"><th className="py-2">Événement</th><th className="text-center">App</th><th className="text-center">Push</th><th className="text-center">E-mail</th></tr></thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.key} className="border-t border-mr-border" data-testid={`matrix-row-${c.key}`}>
                <td className="py-2 pr-2">{c.label}</td>
                {["app", "push"].map((ch) => <td key={ch} className="text-center"><input type="checkbox" data-testid={`matrix-${c.key}-${ch}`} className="w-4 h-4" checked={!!c.channels[ch]} onChange={(e) => set(c.key, ch, e.target.checked)} /></td>)}
                <td className="text-center">
                  <select data-testid={`matrix-${c.key}-email`} className="mr-input h-9 py-0 text-xs w-28" value={String(c.channels.email)} onChange={(e) => set(c.key, "email", e.target.value === "hebdo" ? "hebdo" : e.target.value === "true")}>
                    <option value="true">Oui</option><option value="false">Non</option><option value="hebdo">Résumé hebdo</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button data-testid="matrix-save-button" className="mr-btn-primary mt-4" onClick={save}>Enregistrer</button>
    </section>
  );
}

function Stickers() {
  const [packs, setPacks] = useState(null);
  const [name, setName] = useState("");
  const [urls, setUrls] = useState("");
  const load = () => api.get("/admin/sticker-packs").then(({ data }) => setPacks(data));
  useEffect(() => {
    load();
  }, []);
  const call = (fn, ok) => fn().then(() => { toast.success(ok); load(); }).catch((e) => toast.error(errMsg(e)));
  if (!packs) return <Loader />;
  return (
    <section className="mr-card p-4 mb-8" data-testid="sticker-packs">
      <h2 className="font-medium mb-3">Packs de stickers</h2>
      <ul className="space-y-3 mb-4">
        {packs.map((p) => (
          <li key={p.id} className="border border-mr-border p-3" data-testid="sticker-pack-row">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-sm font-medium">{p.name} {p.default && <span className="text-xs text-mr-muted">(par défaut)</span>} · {p.status}</p>
              <div className="flex gap-2">
                <button data-testid="sticker-pack-toggle" className="mr-btn-ghost px-2 text-xs" onClick={() => call(() => api.put(`/admin/sticker-packs/${p.id}`, { name: p.name, stickers: p.stickers, status: p.status === "actif" ? "inactif" : "actif" }), "Pack mis à jour")}>{p.status === "actif" ? "Désactiver" : "Activer"}</button>
                {!p.default && <button aria-label="Supprimer" data-testid="sticker-pack-delete" className="mr-btn-ghost px-2 text-mr-danger" onClick={() => call(() => api.delete(`/admin/sticker-packs/${p.id}`), "Pack supprimé")}><Trash2 size={14} /></button>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">{p.stickers.map((s) => <img key={s} src={s} alt="" className="w-12 h-12" />)}</div>
          </li>
        ))}
      </ul>
      <p className="text-sm font-medium mb-2">Ajouter un pack</p>
      <input data-testid="sticker-pack-name" className="mr-input mb-2" placeholder="Nom du pack" value={name} onChange={(e) => setName(e.target.value)} />
      <textarea data-testid="sticker-pack-urls" className="mr-input min-h-[80px] mb-2" placeholder="Liens des images (un par ligne, depuis la Médiathèque)" value={urls} onChange={(e) => setUrls(e.target.value)} />
      <button data-testid="sticker-pack-create" className="mr-btn-primary" disabled={name.trim().length < 2 || !urls.trim()}
        onClick={() => call(() => api.post("/admin/sticker-packs", { name, stickers: urls.split("\n").map((u) => u.trim()).filter(Boolean) }), "Pack ajouté").then(() => { setName(""); setUrls(""); })}>Ajouter</button>
    </section>
  );
}

function Announcement() {
  const [title, setTitle] = useState("");
  const create = () => api.post("/conversations/announcement", { title }).then(() => { toast.success("Canal créé"); setTitle(""); }).catch((e) => toast.error(errMsg(e)));
  return (
    <section className="mr-card p-4 mb-8">
      <h2 className="font-medium mb-1 flex items-center gap-2"><Megaphone size={16} /> Canaux d'annonces</h2>
      <p className="text-xs text-mr-muted mb-3">Visibles par tous les membres, en lecture seule. Les administrateurs publient depuis Messages.</p>
      <div className="flex gap-2"><input data-testid="announcement-title-input" className="mr-input" placeholder="Nom du canal" value={title} onChange={(e) => setTitle(e.target.value)} />
        <button data-testid="announcement-create-button" className="mr-btn-primary px-4" disabled={title.trim().length < 2} onClick={create}>Créer</button></div>
    </section>
  );
}

export function MessagingAdmin() {
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Contenu" title="Messagerie et notifications" />
      <MatrixEditor />
      <Announcement />
      <Stickers />
    </div>
  );
}

export function DemoData() {
  const { me } = useAuth();
  const [s, setS] = useState(null);
  const [confirm, setConfirm] = useState("");
  const load = () => api.get("/admin/demo").then(({ data }) => setS(data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  if (!s) return null;
  const purge = async () => {
    try {
      const { data } = await api.post("/admin/demo/purge", { confirm });
      toast.success(`Données de démonstration supprimées (${Object.values(data.deleted).reduce((a, b) => a + b, 0)} éléments).`);
      setConfirm("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="mr-card p-4 mb-10 border-mr-danger/40" data-testid="demo-data-card">
      <h2 className="font-medium mb-1">Données de démonstration</h2>
      {s.purged ? <p className="text-sm text-mr-text2" data-testid="demo-purged">Vidées le {fdatetime(s.purged_at)}. Elles ne seront plus recréées.</p> : (
        <>
          <p className="text-sm text-mr-text2 mb-3" data-testid="demo-counts">{s.users} comptes de démo, {s.articles} articles et guides, {s.operations} opération(s) de démo. Le compte fondateur, les pages du site, les paramètres et le journal d'audit sont conservés.</p>
          {me.user.global_role === "super_admin" ? (
            <div className="flex gap-2">
              <input data-testid="demo-purge-confirm" className="mr-input" placeholder="Tapez VIDER" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              <button data-testid="demo-purge-button" className="mr-btn-secondary px-4 text-mr-danger" disabled={confirm.trim().toUpperCase() !== "VIDER"} onClick={purge}>Vider</button>
            </div>
          ) : <p className="text-xs text-mr-muted">Réservé au fondateur.</p>}
        </>
      )}
    </section>
  );
}
